import { CALENDAR_TIMEZONE } from '@/types/shared';

/**
 * Locale-aware date/number formatting helpers. Numerals stay Western Arabic (0-9) in both
 * languages per 10-design-system.md §2.1, so we pin `numberingSystem: 'latn'` regardless of the
 * active locale rather than letting `ar-EG` switch to Eastern Arabic-Indic digits.
 */

export function intlLocale(language: string): string {
  return language.startsWith('ar') ? 'ar-EG-u-nu-latn' : 'en-GB';
}

export function formatDate(iso: string | null | undefined, language: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(language), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Formats a plain Cairo calendar date (`YYYY-MM-DD`, no time component — e.g.
 * `DashboardSummaryResponse.resolvedPeriod.from/to`, T98) for display. Deliberately parsed as a
 * UTC-midnight instant and rendered with `timeZone: 'UTC'` so the calendar day never shifts
 * depending on the browser's local timezone — we're formatting a date the server already resolved
 * in Africa/Cairo, not re-deriving one, so no local/Cairo TZ math happens here at all.
 */
export function formatDateOnly(dateOnly: string | null | undefined, language: string): string {
  if (!dateOnly) return '';
  const date = new Date(`${dateOnly}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(language), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

export function formatDateTime(iso: string | null | undefined, language: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(language), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatNumber(value: number | null | undefined, language: string): string {
  if (value == null || Number.isNaN(value)) return '';
  return new Intl.NumberFormat(intlLocale(language)).format(value);
}

/** Divisions for `formatRelativeTime`, smallest to largest — classic MDN `Intl.RelativeTimeFormat`
 * cascading-division pattern (seconds -> ... -> years). */
const RELATIVE_TIME_DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

/** Localized relative time ("5 minutes ago" / "قبل 5 دقائق") — used by the dashboard activity
 * feed (`11-dashboard-widget-grid-ux.md §3.2`). Numerals stay Western per `intlLocale`. */
export function formatRelativeTime(iso: string | null | undefined, language: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  let duration = (date.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(intlLocale(language), { numeric: 'auto' });

  for (const division of RELATIVE_TIME_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return rtf.format(Math.round(duration), 'year');
}

/** Bare (currency-suffix-free) 2-decimal amount, Western numerals — the shared building block
 * `formatCurrencyEGP` wraps with the `ج.م`/`EGP` suffix, and calendar chip accessible-name
 * templates (which already append their own "EGP"/"ج.م" word, T104 §2.6) use directly. */
export function formatAmountNumber(amount: number | null | undefined, language: string): string {
  if (amount == null || Number.isNaN(amount)) return '';
  return new Intl.NumberFormat(intlLocale(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatCurrencyEGP(amount: number | null | undefined, language: string): string {
  if (amount == null || Number.isNaN(amount)) return '';
  const formatted = formatAmountNumber(amount, language);
  return language.startsWith('ar') ? `${formatted} ج.م` : `EGP ${formatted}`;
}

/**
 * USD amount formatter — the ONLY place in the app USD is ever displayed (DEC-FIN4, exam pricing's
 * SAT/ACT sections, 17-finance-ux.md §4.4). `$` prefix in both locales (a currency the user only
 * ever references as a pricing input, never holds/spends locally, so no localized suffix is added).
 */
export function formatCurrencyUsd(amount: number | null | undefined, language: string): string {
  if (amount == null || Number.isNaN(amount)) return '';
  const formatted = formatAmountNumber(amount, language);
  return `$${formatted}`;
}

/** Cairo-local wall-clock time (e.g. `10:00 ص` / `10:00 AM`) for a timed calendar event's
 * `startsAt` ISO instant — T104 §2.2/§7.5 "renders using the existing app-wide date/time
 * formatting utility". Explicit `timeZone: CALENDAR_TIMEZONE` (Africa/Cairo) since `startsAt` is a
 * UTC instant and the viewer's browser timezone must never leak into a Cairo-scheduled time. */
export function formatTimeOnly(iso: string | null | undefined, language: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(language), {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: CALENDAR_TIMEZONE,
  }).format(date);
}

/** `{{month}} {{year}}` (long month name) for a `YYYY-MM` month key — the calendar page's header
 * label, live-region announcement, and `<table>` caption (T104 §4.4/§1.3/§7.1). Parsed/rendered as
 * UTC-midnight (no time component) — same "already a resolved calendar unit, no TZ math needed"
 * reasoning as `formatDateOnly`. */
export function formatMonthYear(monthKey: string | null | undefined, language: string): string {
  if (!monthKey) return '';
  const date = new Date(`${monthKey}-01T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(language), {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(date);
}

/** `{{day}} {{month}} {{year}}` (long month name, no weekday) for a Cairo `YYYY-MM-DD` calendar
 * date — the day-detail popover heading and every chip accessible-name template's `{{date}}`
 * (T104 §2.6/§2.7). */
export function formatDayLong(dateOnly: string | null | undefined, language: string): string {
  if (!dateOnly) return '';
  const date = new Date(`${dateOnly}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(language), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** `{{weekday}} {{day}} {{month}}` (no year) for a Cairo `YYYY-MM-DD` calendar date — the mobile
 * agenda view's per-day section header (T104 §4.3), e.g. "Saturday, 4 July" / "السبت، 4 يوليو". */
export function formatWeekdayDayMonth(
  dateOnly: string | null | undefined,
  language: string,
): string {
  if (!dateOnly) return '';
  const date = new Date(`${dateOnly}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(language), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** Age in whole years at a given reference date (defaults to now) — used for the soft "birth date
 * out of the usual 3-25y band" warning, never a blocking validation. */
export function ageInYears(
  dateOfBirthIso: string,
  referenceDate: Date = new Date(),
): number | null {
  const dob = new Date(dateOfBirthIso);
  if (Number.isNaN(dob.getTime())) return null;
  let age = referenceDate.getFullYear() - dob.getFullYear();
  const monthDiff = referenceDate.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}
